import {d as debounce, o as onKeyUpEscape} from '@theme/global';
import '@theme/request-idle-callback';

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce((event, generation) => {
      if (generation !== FacetFiltersForm.submissionGeneration || !event.target.isConnected) return;
      this.onSubmitHandler(event);
    }, 500);
    this.querySelector('form').addEventListener('input', event => {
      // Preserve the new selection while it waits to submit. An older response
      // must not replace its controls or trigger a fallback navigation meanwhile.
      FacetFiltersForm.invalidatePendingWork();
      this.debouncedOnSubmit(event, FacetFiltersForm.submissionGeneration);
    });
    this.querySelector('#FacetsWrapperDesktop')?.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    window.addEventListener('popstate', () => {
      // A pending input must not undo a later history or recovery action.
      FacetFiltersForm.invalidatePendingWork();
      // The URL is authoritative, including entries created outside this component.
      // History navigation must retain the original page and Shopify cursor.
      const searchParams = window.location.search.slice(1);
      // Also restore same-query history entries when they interrupt pending work.
      FacetFiltersForm.renderPage(searchParams, null, false);
    });
    document.addEventListener('click', event => {
      const link = event.target.closest('[data-collection-first-page], [data-collection-clear]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const url = new URL(link.href, window.location.href);
      if (link.hasAttribute('data-collection-first-page')) url.search = window.location.search;
      FacetFiltersForm.navigateToFilter(url, event);
    });
  }

  static invalidatePendingWork() {
    ++FacetFiltersForm.submissionGeneration;
    ++FacetFiltersForm.requestId;
    FacetFiltersForm.controller?.abort();
    FacetFiltersForm.controller = null;
  }

  static resetPagination(parameters) {
    const params = new URLSearchParams(parameters);
    params.delete('page');
    params.delete('phcursor');
    return params;
  }

  static preserveContext(params) {
    const current = new URLSearchParams(window.location.search);
    // These do not change the selected filters. In particular, retain an explicit
    // sort when Shopify's clear-filter URL does not include it.
    for (const name of ['sort_by', 'view', 'preview_theme_id']) {
      if (!params.has(name) && current.has(name)) params.set(name, current.get(name));
    }
    return params;
  }

  static navigateToFilter(url, event) {
    event.preventDefault();
    FacetFiltersForm.invalidatePendingWork();
    url.search = FacetFiltersForm.preserveContext(FacetFiltersForm.resetPagination(url.search)).toString();
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
      // Legacy tag removal changes the path. Let Shopify render the new route.
      FacetFiltersForm.setLoading(false);
      FacetFiltersForm.toggleActiveFacets(false);
      window.location.assign(url.href);
      return;
    }
    FacetFiltersForm.toggleActiveFacets();
    FacetFiltersForm.renderPage(url.search.slice(1));
  }

  static toggleActiveFacets(disabled = true) {
    document.querySelectorAll('.js-facet-remove').forEach(element => element.classList.toggle('disabled', disabled));
  }

  static setLoading(loading) {
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.toggle('loading', loading);
    for (const id of ['ProductCount', 'ProductCountDesktop']) {
      document.getElementById(id)?.classList.toggle('loading', loading);
    }
  }

  static async renderPage(searchParams, event, updateHistory = true) {
    const requestId = ++FacetFiltersForm.requestId;
    FacetFiltersForm.controller?.abort();
    const controller = new AbortController();
    FacetFiltersForm.controller = controller;
    FacetFiltersForm.searchParamsPrev = searchParams;
    if (updateHistory) FacetFiltersForm.updateURLHash(searchParams);
    const destination = window.location.href;
    FacetFiltersForm.setLoading(true);

    try {
      for (const {section} of FacetFiltersForm.getSections()) {
        const url = new URL(destination);
        url.search = searchParams;
        url.searchParams.set('section_id', section);
        const cached = FacetFiltersForm.filterData.find(entry => entry.url === url.href);
        let html = cached?.html;
        if (!cached) {
          const response = await fetch(url.href, {signal: controller.signal});
          if (!response.ok) throw new Error('Collection section request failed');
          html = await response.text();
        }
        if (requestId !== FacetFiltersForm.requestId) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (!parsed.getElementById('ProductGridContainer') || !parsed.getElementById('product-grid') ||
            (document.getElementById('ProductCount') && !parsed.getElementById('ProductCount'))) {
          throw new Error('Collection section response was incomplete');
        }
        if (!cached) FacetFiltersForm.filterData.push({html, url: url.href});
        FacetFiltersForm.renderFilters(parsed, event);
        FacetFiltersForm.renderProductGridContainer(parsed);
        FacetFiltersForm.renderProductCount(parsed);
        FacetFiltersForm.renderMetadata(parsed);
      }
    } catch (error) {
      if (requestId !== FacetFiltersForm.requestId || error.name === 'AbortError') return;
      // Preserve the existing products while Shopify performs a complete request
      // for the selected URL. This also recovers from non-HTML app/error responses.
      window.location.assign(destination);
    } finally {
      if (requestId === FacetFiltersForm.requestId) {
        FacetFiltersForm.setLoading(false);
        FacetFiltersForm.toggleActiveFacets(false);
      }
    }
  }

  static parseResponse(response) {
    return typeof response === 'string' ? new DOMParser().parseFromString(response, 'text/html') : response;
  }

  static renderProductGridContainer(response) {
    const parsed = FacetFiltersForm.parseResponse(response);
    document.getElementById('ProductGridContainer').innerHTML = parsed.getElementById('ProductGridContainer').innerHTML;
    document.dispatchEvent(new CustomEvent('plp:grid-rendered'));
  }

  static renderProductCount(response) {
    const count = FacetFiltersForm.parseResponse(response).getElementById('ProductCount');
    if (!count) return;
    for (const id of ['ProductCount', 'ProductCountDesktop']) {
      const target = document.getElementById(id);
      if (target) target.innerHTML = count.innerHTML;
    }
  }

  static renderMetadata(parsed) {
    const state = parsed.querySelector('#ProductGridContainer [data-collection-state]');
    if (!state) return;
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical && state.dataset.canonical) canonical.href = state.dataset.canonical;
    if (state.dataset.title) document.title = state.dataset.title;
    const robots = document.head.querySelector('meta[data-collection-robots]');
    if (state.dataset.collectionIndexing === 'noindex') {
      const meta = robots || document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex,follow';
      meta.setAttribute('data-collection-robots', '');
      if (!robots) document.head.appendChild(meta);
    } else {
      robots?.remove();
    }
  }

  static renderFilters(parsed, event) {
    const filters = parsed.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');
    const changedFilter = event?.target.closest('.js-filter');
    for (const filter of filters) {
      const current = document.querySelector(`.js-filter[data-index="${filter.dataset.index}"]`);
      if (!current) continue;
      if (changedFilter?.dataset.index === filter.dataset.index) {
        FacetFiltersForm.renderCounts(filter, current);
      } else {
        current.innerHTML = filter.innerHTML;
      }
    }
    FacetFiltersForm.renderActiveFacets(parsed);
    FacetFiltersForm.renderAdditionalElements(parsed);
  }

  static renderActiveFacets(parsed) {
    for (const selector of ['.active-facets-mobile', '.active-facets-desktop']) {
      const incoming = parsed.querySelector(selector);
      const current = document.querySelector(selector);
      if (incoming && current) current.innerHTML = incoming.innerHTML;
    }
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(parsed) {
    for (const selector of ['.mobile-facets__open', '.mobile-facets__count', '.sorting']) {
      const incoming = parsed.querySelector(selector);
      const current = document.querySelector(selector);
      if (incoming && current) current.innerHTML = incoming.innerHTML;
    }
    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents();
    // Keep the other viewport's selected sort in sync for subsequent submissions.
    for (const select of parsed.querySelectorAll('select[name="sort_by"]')) {
      const current = document.getElementById(select.id);
      if (current) current.value = select.value;
    }
  }

  static renderCounts(incoming, current) {
    const count = incoming.querySelector('.facets__selected');
    const target = current.querySelector('.facets__selected');
    if (count && target) target.outerHTML = count.outerHTML;
  }

  static updateURLHash(searchParams) {
    const url = `${window.location.pathname}${searchParams ? '?' + searchParams : ''}${window.location.hash}`;
    if (url !== window.location.pathname + window.location.search + window.location.hash) {
      history.pushState({searchParams}, '', url);
    }
  }

  static getSections() {
    return [{section: document.getElementById('product-grid').dataset.id}];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const params = FacetFiltersForm.resetPagination(new FormData(event.target.closest('form')));
    FacetFiltersForm.renderPage(FacetFiltersForm.preserveContext(params).toString(), event);
  }

  onActiveFilterClick(event) {
    FacetFiltersForm.navigateToFilter(new URL(event.currentTarget.href, window.location.href), event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.requestId = 0;
FacetFiltersForm.submissionGeneration = 0;
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

const applyAvailabilityFilter = () => {
  if (window.theme?.autoApplyAvailabilityFilter !== true) return;
  const collectionState = document.querySelector('#ProductGridContainer [data-collection-state]')?.dataset.collectionState;
  // Keep curated empty collections and temporarily unavailable pages at their
  // original URL instead of manufacturing an empty filtered result.
  if (collectionState === 'empty' || collectionState === 'unavailable') return;
  const params = new URLSearchParams(window.location.search);
  // A direct pagination URL already identifies a particular result set. Applying
  // a new filter here would change that set or strand the visitor beyond its end.
  if (params.has('page') || params.has('phcursor') || params.has('filter.v.availability')) return;
  if (window.location.pathname.includes('/collections/') || window.location.pathname.includes('/search')) {
    params.set('filter.v.availability', '1');
    window.location.replace(`${window.location.pathname}?${params}${window.location.hash}`);
  }
};
applyAvailabilityFilter();

class g extends HTMLElement {
  constructor() {
    super(),
      this.querySelectorAll('input').forEach((e) => e.addEventListener('change', this.onRangeChange.bind(this))),
      this.setMinAndMaxValues();
  }
  onRangeChange(e) {
    this.adjustToValidValues(e.currentTarget), this.setMinAndMaxValues();
  }
  setMinAndMaxValues() {
    const e = this.querySelectorAll('input'),
      n = e[0],
      t = e[1];
    t.value && n.setAttribute('max', t.value),
      n.value && t.setAttribute('min', n.value),
      n.value === '' && t.setAttribute('min', 0),
      t.value === '' && n.setAttribute('max', t.getAttribute('max'));
  }
  adjustToValidValues(e) {
    const n = Number(e.value),
      t = Number(e.getAttribute('min')),
      i = Number(e.getAttribute('max'));
    n < t && (e.value = t), n > i && (e.value = i);
  }
}
customElements.define('price-range', g);
class p extends HTMLElement {
  constructor() {
    super(),
      this.querySelector('a').addEventListener('click', (e) => {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        (this.closest('facet-filters-form') || document.querySelector('facet-filters-form')).onActiveFilterClick(e);
      });
  }
}
customElements.define('facet-remove', p);
